import { useCallback, useRef } from 'react';
import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { move } from '@dnd-kit/helpers';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import { noop } from '@tanstack/react-table';
import { KIcon } from '@karnott/icons';
import {
  BodyCell,
  FooterCell,
  HeaderCell,
  HeaderTitle,
  HeaderWithSort,
  TableContainer,
  TableElement,
  TableRow,
  Tbody,
  Tfoot,
  Thead,
} from '../components';
import { CustomColumnDef, FormTableProps, RowId, RowShape } from '../types';
import { useTable, useTotalRow } from './hooks';

type ColumnHeaderProps<Row extends RowShape> = {
  columnDef: CustomColumnDef<Row>;
  Icon?: KIcon;
  background?: string;
  multiline: boolean;
};

function ColumnHeader<Row extends RowShape>({ columnDef, Icon, background, multiline }: ColumnHeaderProps<Row>) {
  const headerTitle = useRef<HTMLSpanElement>(null);

  const width = columnDef.width;

  return (
    <HeaderCell
      title={(headerTitle.current?.innerText ? headerTitle.current?.textContent : null) || undefined}
      background={background}
      canSort={false}
      $width={width}
    >
      <HeaderWithSort>
        {Icon ? <Icon size={16} color="currentColor" /> : null}
        <HeaderTitle ref={headerTitle} multiline={multiline}>
          {columnDef.renderHeader()}
        </HeaderTitle>
      </HeaderWithSort>
    </HeaderCell>
  );
}

type RowProps<Row extends RowShape> = {
  row: Row;
  columnDefs: CustomColumnDef<Row>[];
  index: number;
  highlightOnHover: boolean;
  onHover: ((rowId: RowId | null) => void) | undefined;
  enableDragAndDrop: boolean;
  containerElement: HTMLElement | null;
};

function Row<Row extends RowShape>({
  row,
  columnDefs,
  index,
  highlightOnHover,
  onHover,
  enableDragAndDrop,
  containerElement,
}: RowProps<Row>) {
  const { ref: dragRef } = useSortable({
    id: row.id,
    index,
    disabled: !enableDragAndDrop,
    modifiers: [RestrictToElement.configure({ element: containerElement })],
  });
  const refCB = useCallback(
    (node: Element | null) => {
      dragRef(enableDragAndDrop ? node : null);
    },
    [dragRef, enableDragAndDrop],
  );

  return (
    <TableRow
      data-index={index}
      highlightOnHover={highlightOnHover}
      onMouseEnter={() => row && onHover?.(row.id)}
      onMouseLeave={() => row && onHover?.(null)}
      ref={refCB}
      role="row"
    >
      {columnDefs.map((columnDef) => (
        <BodyCell key={columnDef.id} className={columnDef.displayOnHover ? 'cell_on_hover' : ''}>
          {columnDef.renderCell(row)}
        </BodyCell>
      ))}
    </TableRow>
  );
}

const empty: never[] = [];

/**
 * Table to display interactive forms, with inputs, list selectors etc. The virtualization is always disabled and the
 * custom rendering engine of `Table` is disabled as well to allow to seamlessly update the rows from within themselves.
 * Columns cannot be sorted using this component.
 */
export function FormTable<Row extends RowShape>({
  columns = empty,
  rows = empty,
  displayTotal = false,
  selection,
  onSelectionChange = noop,
  onRowHover,
  Icon,
  headerColor,
  highlightOnHover = true,
  multilineHeaders = false,
  draggableRows = false,
  disableDragOnRowIndex,
  onRowDrop = noop,
}: FormTableProps<Row>) {
  /* Table initialization */
  const { columnDefs } = useTable({
    columns,
    rows,
    selection,
    onSelectionChange,
  });

  /* Total footer cells */
  const totalCells = useTotalRow({ rows, columnDefs, displayTotal });

  const hasDefinedWidth = columns.some((c) => c?.width != undefined);

  const tbodyElement = useRef<HTMLTableSectionElement>(null);

  return (
    <TableContainer>
      <TableElement fixedLayout={hasDefinedWidth}>
        <Thead background={headerColor}>
          <tr>
            {columnDefs.map((columnDef, i) => (
              <ColumnHeader
                key={columnDef.id}
                columnDef={columnDef}
                Icon={i === 0 ? Icon : undefined}
                background={headerColor}
                multiline={multilineHeaders}
              />
            ))}
          </tr>
        </Thead>

        <Tbody ref={tbodyElement}>
          <DragDropProvider
            modifiers={[RestrictToVerticalAxis]}
            onDragEnd={(e) => onRowDrop(move(rows, e).map((r) => r.id))}
          >
            {rows.map((row, i) => (
              <Row
                key={row.id}
                row={row}
                columnDefs={columnDefs}
                index={i}
                highlightOnHover={highlightOnHover}
                onHover={onRowHover}
                enableDragAndDrop={
                  draggableRows &&
                  (disableDragOnRowIndex !== undefined ? disableDragOnRowIndex.includes(i) === false : true)
                }
                containerElement={tbodyElement.current}
              />
            ))}
          </DragDropProvider>
        </Tbody>

        {displayTotal ? (
          <Tfoot>
            <tr>
              <FooterCell as="th">Total</FooterCell>
              {totalCells.map((cell, i) => {
                const columnDef = columnDefs.at(i + 1)!;
                const renderFn = cell ? columnDef.renderCell : null;
                return (
                  <FooterCell key={columnDef.id}>
                    {renderFn && cell ? renderFn({ [columnDef.id]: cell } as Row) : null}
                  </FooterCell>
                );
              })}
            </tr>
          </Tfoot>
        ) : null}
      </TableElement>
    </TableContainer>
  );
}
